<script setup lang="ts">
  import { inject, type Ref } from 'vue'
  import { type ClientOrganisation } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'

  const org = inject<Ref<ClientOrganisation | null>>('org')

  const { t } = useTranslation({
    en: {
      organizationLogo: 'Organization Logo',
      contactUsForMoreInfo: 'Contact us for more information'
    },
    de: {
      organizationLogo: 'Organisationslogo',
      contactUsForMoreInfo: 'Kontaktieren Sie uns für weitere Informationen'
    }
  })

  function hasOrgLogo() {
    return org?.value?.logo && org.value.logo !== ''
  }
</script>

<template>
  <div
    v-if="org"
    class="space-y-4 rounded-lg bg-gray-100 p-3"
    role="region"
    :aria-label="org.name || t('organizationLogo')"
  >
    <div class="flex flex-col items-center justify-center p-6">
      <div
        v-if="hasOrgLogo()"
        class="text-sm text-gray-900"
      >
        <img
          :src="org.logo || ''"
          :alt="org.name || ''"
          class="max-h-16 max-w-full"
          data-cy="ClientPortalOrganisationCardLogo"
        />
      </div>
      <div
        v-else
        data-cy="ClientPortalOrganisationCardName"
        class="text-sm font-medium text-gray-900"
      >
        {{ org.name || t('organizationLogo') }}
      </div>
    </div>

    <!-- Organization description -->
    <div
      v-if="org.description"
      data-cy="ClientPortalOrganisationCardDescription"
      class="whitespace-pre-line text-sm leading-relaxed text-gray-800"
    >
      {{ org.description }}
    </div>

    <div
      v-else
      class="text-sm leading-relaxed text-gray-700"
    >
      {{ t('contactUsForMoreInfo') }}
    </div>
  </div>
</template>
